export interface BrainwaveControlPoint {
  time: number;
  carrierFreq: number;
  brainwaveFreq: number;
  amplitude: number;
}

export type ModulationType = 'binaural' | 'amplitude' | 'frequency' | 'stereo' | 'isochronic';

export interface BrainwaveOscillatorConfig {
  audioContext: BaseAudioContext;
  modulationType: ModulationType;
  controlPoints: BrainwaveControlPoint[];
  duration: number;
  initialVolume?: number; // Optional initial volume override
  trackVolume?: number; // Track-level volume (0-1)
}

export class BrainwaveOscillatorNode {
  private audioContext: BaseAudioContext;
  private modulationType: ModulationType;
  private controlPoints: BrainwaveControlPoint[];
  private duration: number;
  private trackVolume: number;
  
  private leftOscillator: OscillatorNode | null = null;
  private rightOscillator: OscillatorNode | null = null;
  private leftGain: GainNode | null = null;
  private rightGain: GainNode | null = null;
  private merger: ChannelMergerNode | null = null;
  private outputGain: GainNode;
  
  private lfoOscillator: OscillatorNode | null = null;
  private lfoGain: GainNode | null = null;
  private lfoOffset: ConstantSourceNode | null = null;
  private envelopeShaper: WaveShaperNode | null = null;
  
  private startTime: number = 0;
  private isPlaying: boolean = false;

  constructor(config: BrainwaveOscillatorConfig) {
    this.audioContext = config.audioContext;
    this.modulationType = config.modulationType;
    this.controlPoints = config.controlPoints;
    this.duration = config.duration;
    this.trackVolume = config.trackVolume ?? 1.0;
    
    this.outputGain = this.audioContext.createGain();
    // Use initialVolume if provided, otherwise set to 1.0 (let trackVolume handle the actual volume control)
    const initialAmplitude = config.initialVolume !== undefined 
      ? config.initialVolume 
      : 1.0;
    this.outputGain.gain.value = initialAmplitude;
  }

  private createOscillators(): void {
    // Create stereo oscillators
    this.leftOscillator = this.audioContext.createOscillator();
    this.rightOscillator = this.audioContext.createOscillator();
    
    this.leftGain = this.audioContext.createGain();
    this.rightGain = this.audioContext.createGain();
    
    this.merger = this.audioContext.createChannelMerger(2);
    
    // Connect oscillators to gains
    this.leftOscillator.connect(this.leftGain);
    this.rightOscillator.connect(this.rightGain);
    
    // Connect to channel merger
    this.leftGain.connect(this.merger, 0, 0);
    this.rightGain.connect(this.merger, 0, 1);
    
    // Connect merger to output
    this.merger.connect(this.outputGain);
    
    // Set oscillator type
    this.leftOscillator.type = 'sine';
    this.rightOscillator.type = 'sine';
    
    // Setup modulation-specific nodes
    if (this.modulationType === 'amplitude' || this.modulationType === 'stereo' || this.modulationType === 'frequency' || this.modulationType === 'isochronic') {
      this.lfoOscillator = this.audioContext.createOscillator();
      this.lfoGain = this.audioContext.createGain();
      
      // Set up LFO with appropriate wave type
      if (this.modulationType === 'isochronic') {
        this.lfoOscillator.type = 'sine'; // Use sine wave for smoother modulation
        
        // Create a wave shaper to transform sine wave into smooth pulses
        this.envelopeShaper = this.audioContext.createWaveShaper();
        this.envelopeShaper.curve = this.createIsochronicCurve();
        this.envelopeShaper.oversample = '4x'; // Higher quality processing
        
        this.lfoOscillator.connect(this.envelopeShaper);
        this.envelopeShaper.connect(this.lfoGain);
      } else {
        this.lfoOscillator.type = 'sine'; // Smooth for other types
        this.lfoOscillator.connect(this.lfoGain);
      }
      
      // For amplitude and isochronic modulation, create the offset source
      if (this.modulationType === 'amplitude' || this.modulationType === 'isochronic') {
        this.lfoOffset = this.audioContext.createConstantSource();
        // The ConstantSourceNode provides a DC offset to convert bipolar (-1,+1) to unipolar (0,1)
        this.lfoOffset.offset.value = 1; // This creates the base level
      }
    }
  }

  private setInitialValues(values: { carrierFreq: number; brainwaveFreq: number; amplitude: number }, currentTime: number): void {
    if (!this.leftOscillator || !this.rightOscillator || !this.leftGain || !this.rightGain) return;
    
    switch (this.modulationType) {
      case 'binaural':
        // Left ear gets carrier frequency
        this.leftOscillator.frequency.setValueAtTime(values.carrierFreq, currentTime);
        // Right ear gets carrier + brainwave frequency
        this.rightOscillator.frequency.setValueAtTime(values.carrierFreq + values.brainwaveFreq, currentTime);
        // Set amplitude from control point multiplied by track volume
        const binauralGain = values.amplitude * this.trackVolume;
        this.leftGain.gain.setValueAtTime(binauralGain, currentTime);
        this.rightGain.gain.setValueAtTime(binauralGain, currentTime);
        break;
        
      case 'amplitude':
        // Both channels get same carrier frequency
        this.leftOscillator.frequency.setValueAtTime(values.carrierFreq, currentTime);
        this.rightOscillator.frequency.setValueAtTime(values.carrierFreq, currentTime);
        // Set base gain to 0 for amplitude modulation
        this.leftGain.gain.setValueAtTime(0, currentTime);
        this.rightGain.gain.setValueAtTime(0, currentTime);
        // Set LFO parameters with track volume applied
        if (this.lfoOscillator && this.lfoGain && this.lfoOffset) {
          this.lfoOscillator.frequency.setValueAtTime(values.brainwaveFreq, currentTime);
          const amplitudeGain = values.amplitude * this.trackVolume;
          this.lfoGain.gain.setValueAtTime(amplitudeGain * 0.2, currentTime);
          this.lfoOffset.offset.setValueAtTime(amplitudeGain * 0.8, currentTime);
        }
        break;
        
      case 'frequency':
        // Set carrier frequency
        this.leftOscillator.frequency.setValueAtTime(values.carrierFreq, currentTime);
        this.rightOscillator.frequency.setValueAtTime(values.carrierFreq, currentTime);
        // Set amplitude from control point multiplied by track volume
        const frequencyGain = values.amplitude * this.trackVolume;
        this.leftGain.gain.setValueAtTime(frequencyGain, currentTime);
        this.rightGain.gain.setValueAtTime(frequencyGain, currentTime);
        // Set LFO parameters
        if (this.lfoOscillator && this.lfoGain) {
          this.lfoOscillator.frequency.setValueAtTime(values.brainwaveFreq, currentTime);
          this.lfoGain.gain.setValueAtTime(10, currentTime);
        }
        break;
        
      case 'stereo':
        // Set carrier frequency
        this.leftOscillator.frequency.setValueAtTime(values.carrierFreq, currentTime);
        this.rightOscillator.frequency.setValueAtTime(values.carrierFreq, currentTime);
        // Set base gain levels using amplitude multiplied by track volume
        const stereoBaseGain = values.amplitude * this.trackVolume * 0.5;
        this.leftGain.gain.setValueAtTime(stereoBaseGain, currentTime);
        this.rightGain.gain.setValueAtTime(stereoBaseGain, currentTime);
        // Set LFO parameters (modulation depth proportional to amplitude and track volume)
        if (this.lfoOscillator && this.lfoGain) {
          this.lfoOscillator.frequency.setValueAtTime(values.brainwaveFreq, currentTime);
          this.lfoGain.gain.setValueAtTime(values.amplitude * this.trackVolume * 0.25, currentTime);
        }
        break;
        
      case 'isochronic':
        // Set carrier frequency
        this.leftOscillator.frequency.setValueAtTime(values.carrierFreq, currentTime);
        this.rightOscillator.frequency.setValueAtTime(values.carrierFreq, currentTime);
        // Set base gain to 0 for isochronic modulation
        this.leftGain.gain.setValueAtTime(0, currentTime);
        this.rightGain.gain.setValueAtTime(0, currentTime);
        // Set LFO parameters with track volume applied
        if (this.lfoOscillator && this.lfoGain) {
          this.lfoOscillator.frequency.setValueAtTime(values.brainwaveFreq / 2, currentTime);
          this.lfoGain.gain.setValueAtTime(values.amplitude * this.trackVolume, currentTime);
        }
        break;
    }
  }

  private createIsochronicCurve(): Float32Array {
    // Creates proper isochronic pulses: one pulse per cycle with true silence between
    const samples = 65536;
    const curve = new Float32Array(samples);
    
    for (let i = 0; i < samples; i++) {
      const x = (i - samples / 2) / (samples / 2); // Range: -1 to 1
      
      // Map x to a 0-1 cycle (so we get only one pulse per sine wave cycle, not two)
      const normalizedPhase = (x + 1) / 2; // Convert -1,1 to 0,1
      
      // Create one pulse per cycle in the first half only
      let value = 0.0; // Default to silence
      
      if (normalizedPhase >= 0.25 && normalizedPhase <= 0.75) {
        // Create pulse in the middle portion of the cycle
        const pulsePhase = (normalizedPhase - 0.25) / 0.5; // Map to 0-1 within pulse region
        
        const attackDecayWidth = 0.2; // 20% of pulse for attack/decay
        
        if (pulsePhase <= attackDecayWidth) {
          // Attack phase - smooth rise from 0 to 1
          const attackProgress = pulsePhase / attackDecayWidth;
          value = 0.5 * (1 - Math.cos(attackProgress * Math.PI)); // Smooth cosine curve
        } else if (pulsePhase >= 1 - attackDecayWidth) {
          // Decay phase - smooth fall from 1 to 0  
          const decayProgress = (pulsePhase - (1 - attackDecayWidth)) / attackDecayWidth;
          value = 0.5 * (1 + Math.cos(decayProgress * Math.PI)); // Smooth cosine curve
        } else {
          // Sustain phase - full level
          value = 1.0;
        }
      }
      // else remains 0.0 for silence
      
      curve[i] = value;
    }
    
    return curve;
  }

  private scheduleParameterChanges(startTime: number, currentTime: number): void {
    if (!this.leftOscillator || !this.rightOscillator || !this.leftGain || !this.rightGain) return;
    
    // Sort control points by time
    const sortedPoints = [...this.controlPoints].sort((a, b) => a.time - b.time);
    
    // Calculate offset from start time to get current position in the track
    const offsetTime = Math.max(0, currentTime - startTime);
    
    // Get interpolated values for the current time
    const currentValues = this.getInterpolatedValues(offsetTime);
    
    // Set initial values based on current position
    this.setInitialValues(currentValues, currentTime);
    
    // Schedule parameter changes for each control point
    for (let i = 0; i < sortedPoints.length; i++) {
      const point = sortedPoints[i];
      const pointTime = startTime + point.time;
      
      // Skip points that are in the past
      if (pointTime < currentTime) continue;
      
      switch (this.modulationType) {
        case 'binaural':
          // Left ear gets carrier frequency
          if (i === 0 || pointTime <= currentTime) {
            this.leftOscillator.frequency.setValueAtTime(point.carrierFreq, Math.max(pointTime, currentTime));
          } else {
            this.leftOscillator.frequency.linearRampToValueAtTime(point.carrierFreq, pointTime);
          }
          
          // Right ear gets carrier + brainwave frequency
          const rightFreq = point.carrierFreq + point.brainwaveFreq;
          if (i === 0 || pointTime <= currentTime) {
            this.rightOscillator.frequency.setValueAtTime(rightFreq, Math.max(pointTime, currentTime));
          } else {
            this.rightOscillator.frequency.linearRampToValueAtTime(rightFreq, pointTime);
          }
          
          // Set amplitude from control point multiplied by track volume
          const binauralGain = point.amplitude * this.trackVolume;
          if (i === 0 || pointTime <= currentTime) {
            this.leftGain.gain.setValueAtTime(binauralGain, Math.max(pointTime, currentTime));
            this.rightGain.gain.setValueAtTime(binauralGain, Math.max(pointTime, currentTime));
          } else {
            this.leftGain.gain.linearRampToValueAtTime(binauralGain, pointTime);
            this.rightGain.gain.linearRampToValueAtTime(binauralGain, pointTime);
          }
          break;
          
        case 'amplitude':
          // Both channels get same carrier frequency
          if (i === 0 || pointTime <= currentTime) {
            this.leftOscillator.frequency.setValueAtTime(point.carrierFreq, Math.max(pointTime, currentTime));
            this.rightOscillator.frequency.setValueAtTime(point.carrierFreq, Math.max(pointTime, currentTime));
          } else {
            this.leftOscillator.frequency.linearRampToValueAtTime(point.carrierFreq, pointTime);
            this.rightOscillator.frequency.linearRampToValueAtTime(point.carrierFreq, pointTime);
          }
          
          // For amplitude modulation, set base gain to 0 since we're using offset + LFO
          if (i === 0 || pointTime <= currentTime) {
            this.leftGain.gain.setValueAtTime(0, Math.max(pointTime, currentTime));
            this.rightGain.gain.setValueAtTime(0, Math.max(pointTime, currentTime));
          }
          
          // LFO creates smooth amplitude modulation with track volume applied
          if (this.lfoOscillator && this.lfoGain && this.lfoOffset) {
            const amplitudeGain = point.amplitude * this.trackVolume;
            if (i === 0 || pointTime <= currentTime) {
              this.lfoOscillator.frequency.setValueAtTime(point.brainwaveFreq, Math.max(pointTime, currentTime));
              // Set modulation depth (20% of base amplitude for smooth pulsing)
              this.lfoGain.gain.setValueAtTime(amplitudeGain * 0.2, Math.max(pointTime, currentTime));
              // Set base level via offset (80% of amplitude so total can reach 100%)
              this.lfoOffset.offset.setValueAtTime(amplitudeGain * 0.8, Math.max(pointTime, currentTime));
            } else {
              this.lfoOscillator.frequency.linearRampToValueAtTime(point.brainwaveFreq, pointTime);
              this.lfoGain.gain.linearRampToValueAtTime(amplitudeGain * 0.2, pointTime);
              this.lfoOffset.offset.linearRampToValueAtTime(amplitudeGain * 0.8, pointTime);
            }
          }
          break;
          
        case 'frequency':
          // Frequency modulation - LFO modulates the carrier frequency
          const frequencyGain = point.amplitude * this.trackVolume;
          if (i === 0 || pointTime <= currentTime) {
            this.leftOscillator.frequency.setValueAtTime(point.carrierFreq, Math.max(pointTime, currentTime));
            this.rightOscillator.frequency.setValueAtTime(point.carrierFreq, Math.max(pointTime, currentTime));
            this.leftGain.gain.setValueAtTime(frequencyGain, Math.max(pointTime, currentTime));
            this.rightGain.gain.setValueAtTime(frequencyGain, Math.max(pointTime, currentTime));
          } else {
            this.leftOscillator.frequency.linearRampToValueAtTime(point.carrierFreq, pointTime);
            this.rightOscillator.frequency.linearRampToValueAtTime(point.carrierFreq, pointTime);
            this.leftGain.gain.linearRampToValueAtTime(frequencyGain, pointTime);
            this.rightGain.gain.linearRampToValueAtTime(frequencyGain, pointTime);
          }
          
          // LFO creates frequency modulation
          if (this.lfoOscillator && this.lfoGain) {
            if (i === 0 || pointTime <= currentTime) {
              this.lfoOscillator.frequency.setValueAtTime(point.brainwaveFreq, Math.max(pointTime, currentTime));
              // Set modulation depth (10Hz deviation creates nice FM effect)
              this.lfoGain.gain.setValueAtTime(10, Math.max(pointTime, currentTime));
            } else {
              this.lfoOscillator.frequency.linearRampToValueAtTime(point.brainwaveFreq, pointTime);
              this.lfoGain.gain.linearRampToValueAtTime(10, pointTime);
            }
          }
          break;
          
        case 'stereo':
          // Stereo panning modulation - both channels same frequency
          if (i === 0 || pointTime <= currentTime) {
            this.leftOscillator.frequency.setValueAtTime(point.carrierFreq, Math.max(pointTime, currentTime));
            this.rightOscillator.frequency.setValueAtTime(point.carrierFreq, Math.max(pointTime, currentTime));
          } else {
            this.leftOscillator.frequency.linearRampToValueAtTime(point.carrierFreq, pointTime);
            this.rightOscillator.frequency.linearRampToValueAtTime(point.carrierFreq, pointTime);
          }
          
          // Set base amplitude levels using control point amplitude multiplied by track volume
          const stereoBaseGain = point.amplitude * this.trackVolume * 0.5;
          if (i === 0 || pointTime <= currentTime) {
            this.leftGain.gain.setValueAtTime(stereoBaseGain, Math.max(pointTime, currentTime));
            this.rightGain.gain.setValueAtTime(stereoBaseGain, Math.max(pointTime, currentTime));
          } else {
            this.leftGain.gain.linearRampToValueAtTime(stereoBaseGain, pointTime);
            this.rightGain.gain.linearRampToValueAtTime(stereoBaseGain, pointTime);
          }
          
          // LFO creates stereo panning effect (modulation depth proportional to amplitude and track volume)
          if (this.lfoOscillator && this.lfoGain) {
            const stereoModDepth = point.amplitude * this.trackVolume * 0.25;
            if (i === 0 || pointTime <= currentTime) {
              this.lfoOscillator.frequency.setValueAtTime(point.brainwaveFreq, Math.max(pointTime, currentTime));
              // Set modulation depth proportional to amplitude and track volume
              this.lfoGain.gain.setValueAtTime(stereoModDepth, Math.max(pointTime, currentTime));
            } else {
              this.lfoOscillator.frequency.linearRampToValueAtTime(point.brainwaveFreq, pointTime);
              this.lfoGain.gain.linearRampToValueAtTime(stereoModDepth, pointTime);
            }
          }
          break;
          
        case 'isochronic':
          // Isochronic tones - rhythmic pulses with true silence between
          if (i === 0 || pointTime <= currentTime) {
            this.leftOscillator.frequency.setValueAtTime(point.carrierFreq, Math.max(pointTime, currentTime));
            this.rightOscillator.frequency.setValueAtTime(point.carrierFreq, Math.max(pointTime, currentTime));
          } else {
            this.leftOscillator.frequency.linearRampToValueAtTime(point.carrierFreq, pointTime);
            this.rightOscillator.frequency.linearRampToValueAtTime(point.carrierFreq, pointTime);
          }
          
          // For isochronic, set base gain to 0 - the shaped LFO will control everything
          if (i === 0 || pointTime <= currentTime) {
            this.leftGain.gain.setValueAtTime(0, Math.max(pointTime, currentTime));
            this.rightGain.gain.setValueAtTime(0, Math.max(pointTime, currentTime));
          }
          
          // Shaped LFO creates smooth pulses with true silence (with track volume applied)
          if (this.lfoOscillator && this.lfoGain) {
            const isochronicGain = point.amplitude * this.trackVolume;
            if (i === 0 || pointTime <= currentTime) {
              // Run LFO at half frequency to get one pulse per cycle instead of two
              this.lfoOscillator.frequency.setValueAtTime(point.brainwaveFreq / 2, Math.max(pointTime, currentTime));
              // Apply amplitude with track volume
              this.lfoGain.gain.setValueAtTime(isochronicGain, Math.max(pointTime, currentTime));
            } else {
              this.lfoOscillator.frequency.linearRampToValueAtTime(point.brainwaveFreq / 2, pointTime);
              this.lfoGain.gain.linearRampToValueAtTime(isochronicGain, pointTime);
            }
          }
          break;
      }
    }
    
    // Apply fade in/out (but don't override manual volume setting)
  //   const fadeTime = 0.1; // 100ms fade
  //   const currentVolume = this.outputGain.gain.value; // Preserve any manually set volume
  //   this.outputGain.gain.setValueAtTime(0, Math.max(startTime, currentTime));
  //   this.outputGain.gain.linearRampToValueAtTime(currentVolume, Math.max(startTime + fadeTime, currentTime + 0.01));
  //   this.outputGain.gain.linearRampToValueAtTime(currentVolume, startTime + this.duration - fadeTime);
  //   this.outputGain.gain.linearRampToValueAtTime(0, startTime + this.duration);
   }

  start(when: number = 0, offset: number = 0): void {
    if (this.isPlaying) return;
    
    this.createOscillators();
    
    const currentTime = this.audioContext.currentTime;
    this.startTime = when || currentTime;
    
    // Apply modulation connections based on type
    if (this.modulationType === 'amplitude' && this.lfoOscillator && this.lfoGain && this.lfoOffset && this.leftGain && this.rightGain) {
      // Create smooth unipolar amplitude modulation
      // LFO output: -1 to +1, scaled by lfoGain
      // Offset: adds constant value to create 0 to 1 range
      // Result: smooth pulsing without harsh cuts to zero
      
      // Connect both LFO and offset to the gain nodes
      this.lfoGain.connect(this.leftGain.gain);
      this.lfoGain.connect(this.rightGain.gain);
      this.lfoOffset.connect(this.leftGain.gain);
      this.lfoOffset.connect(this.rightGain.gain);
      
      // Start the offset source
      this.lfoOffset.start(this.startTime);
    } else if (this.modulationType === 'isochronic' && this.lfoOscillator && this.lfoGain && this.leftGain && this.rightGain) {
      // Create isochronic pulses - NO offset source for true silence between pulses
      
      // Connect only the shaped LFO to the gain nodes (no offset for true silence)
      this.lfoGain.connect(this.leftGain.gain);
      this.lfoGain.connect(this.rightGain.gain);
    } else if (this.modulationType === 'frequency' && this.lfoOscillator && this.lfoGain && this.leftOscillator && this.rightOscillator) {
      // Create frequency modulation by connecting LFO to oscillator frequency
      // This will make the carrier frequency wobble at the brainwave rate
      this.lfoGain.connect(this.leftOscillator.frequency);
      this.lfoGain.connect(this.rightOscillator.frequency);
    } else if (this.modulationType === 'stereo' && this.lfoOscillator && this.lfoGain && this.leftGain && this.rightGain) {
      // Create stereo panning by connecting LFO to both channels with opposite polarity
      // Left channel gets positive modulation, right channel gets negative (inverted)
      
      // Create an inverter gain node for the right channel
      const inverterGain = this.audioContext.createGain();
      inverterGain.gain.value = -1; // Invert the signal
      
      // Connect LFO to left channel directly (when LFO is positive, left gets louder)
      this.lfoGain.connect(this.leftGain.gain);
      
      // Connect LFO to right channel through inverter (when LFO is positive, right gets quieter)
      this.lfoGain.connect(inverterGain);
      inverterGain.connect(this.rightGain.gain);
    }
    
    // Schedule parameter changes
    this.scheduleParameterChanges(this.startTime - offset, currentTime);
    
    // Start oscillators
    if (this.leftOscillator && this.rightOscillator) {
      this.leftOscillator.start(this.startTime);
      this.rightOscillator.start(this.startTime);
      
      // Stop at duration
      this.leftOscillator.stop(this.startTime + this.duration - offset);
      this.rightOscillator.stop(this.startTime + this.duration - offset);
    }
    
    if (this.lfoOscillator) {
      this.lfoOscillator.start(this.startTime);
      this.lfoOscillator.stop(this.startTime + this.duration - offset);
    }
    
    this.isPlaying = true;
  }

  stop(when: number = 0): void {
    if (!this.isPlaying) return;
    
    const stopTime = when || this.audioContext.currentTime;
    
    if (this.leftOscillator && this.rightOscillator) {
      try {
        this.leftOscillator.stop(stopTime);
        this.rightOscillator.stop(stopTime);
      } catch (e) {
        // Oscillator may have already stopped
      }
    }
    
    if (this.lfoOscillator) {
      try {
        this.lfoOscillator.stop(stopTime);
      } catch (e) {
        // Oscillator may have already stopped
      }
    }
    
    this.isPlaying = false;
    this.cleanup();
  }

  connect(destination: AudioNode): void {
    this.outputGain.connect(destination);
  }

  disconnect(): void {
    this.outputGain.disconnect();
  }

  setTrackVolume(volume: number): void {
    this.trackVolume = volume;
    
    // Update all gain values in real-time if oscillator is playing
    if (!this.isPlaying || !this.leftGain || !this.rightGain) return;
    
    const currentTime = this.audioContext.currentTime;
    const offsetTime = currentTime - this.startTime;
    
    // Get current interpolated values
    const currentValues = this.getInterpolatedValues(offsetTime);
    
    // Update gains based on current modulation type
    switch (this.modulationType) {
      case 'binaural':
      case 'frequency':
        const gainValue = currentValues.amplitude * this.trackVolume;
        this.leftGain.gain.setValueAtTime(gainValue, currentTime);
        this.rightGain.gain.setValueAtTime(gainValue, currentTime);
        break;
        
      case 'stereo':
        const stereoGain = currentValues.amplitude * this.trackVolume * 0.5;
        this.leftGain.gain.setValueAtTime(stereoGain, currentTime);
        this.rightGain.gain.setValueAtTime(stereoGain, currentTime);
        // Update LFO modulation depth
        if (this.lfoGain) {
          this.lfoGain.gain.setValueAtTime(currentValues.amplitude * this.trackVolume * 0.25, currentTime);
        }
        break;
        
      case 'amplitude':
        // Update LFO parameters
        if (this.lfoGain && this.lfoOffset) {
          const amplitudeGain = currentValues.amplitude * this.trackVolume;
          this.lfoGain.gain.setValueAtTime(amplitudeGain * 0.2, currentTime);
          this.lfoOffset.offset.setValueAtTime(amplitudeGain * 0.8, currentTime);
        }
        break;
        
      case 'isochronic':
        // Update LFO gain
        if (this.lfoGain) {
          this.lfoGain.gain.setValueAtTime(currentValues.amplitude * this.trackVolume, currentTime);
        }
        break;
    }
  }

  setVolume(volume: number): void {
    //console.log('setVolume', volume);
    this.outputGain.gain.value = volume;
  }

  private cleanup(): void {
    // Disconnect all nodes
    if (this.leftOscillator) {
      this.leftOscillator.disconnect();
      this.leftOscillator = null;
    }
    if (this.rightOscillator) {
      this.rightOscillator.disconnect();
      this.rightOscillator = null;
    }
    if (this.leftGain) {
      this.leftGain.disconnect();
      this.leftGain = null;
    }
    if (this.rightGain) {
      this.rightGain.disconnect();
      this.rightGain = null;
    }
    if (this.merger) {
      this.merger.disconnect();
      this.merger = null;
    }
    if (this.lfoOscillator) {
      this.lfoOscillator.disconnect();
      this.lfoOscillator = null;
    }
    if (this.lfoGain) {
      this.lfoGain.disconnect();
      this.lfoGain = null;
    }
    if (this.lfoOffset) {
      this.lfoOffset.disconnect();
      this.lfoOffset = null;
    }
    if (this.envelopeShaper) {
      this.envelopeShaper.disconnect();
      this.envelopeShaper = null;
    }
  }

  getOutputNode(): GainNode {
    return this.outputGain;
  }

  updateControlPoints(controlPoints: BrainwaveControlPoint[]): void {
    this.controlPoints = controlPoints;
    
    // For real-time updates, we need to update the oscillator frequencies directly
    // This only works reliably for certain parameters
    if (this.isPlaying && this.leftOscillator && this.rightOscillator && controlPoints.length > 0) {
      const currentPoint = controlPoints[0]; // Use first control point for immediate update
      const currentTime = this.audioContext.currentTime;
      
      try {
        // Cancel any previously scheduled changes and set new values
        this.leftOscillator.frequency.cancelScheduledValues(currentTime);
        this.rightOscillator.frequency.cancelScheduledValues(currentTime);
        
        if (this.leftGain && this.rightGain) {
          this.leftGain.gain.cancelScheduledValues(currentTime);
          this.rightGain.gain.cancelScheduledValues(currentTime);
        }
        
        // Apply the new values based on modulation type
        switch (this.modulationType) {
          case 'binaural':
            // Left ear gets carrier frequency
            this.leftOscillator.frequency.setTargetAtTime(
              currentPoint.carrierFreq, 
              currentTime, 
              0.1 // 100ms smooth transition
            );
            
            // Right ear gets carrier + brainwave frequency
            this.rightOscillator.frequency.setTargetAtTime(
              currentPoint.carrierFreq + currentPoint.brainwaveFreq, 
              currentTime, 
              0.1
            );
            
            // Update amplitude using control point amplitude multiplied by track volume
            if (this.leftGain && this.rightGain) {
              const gain = currentPoint.amplitude * this.trackVolume;
              this.leftGain.gain.setTargetAtTime(gain, currentTime, 0.1);
              this.rightGain.gain.setTargetAtTime(gain, currentTime, 0.1);
            }
            break;
            
          case 'amplitude':
            // Both channels get same carrier frequency
            this.leftOscillator.frequency.setTargetAtTime(currentPoint.carrierFreq, currentTime, 0.1);
            this.rightOscillator.frequency.setTargetAtTime(currentPoint.carrierFreq, currentTime, 0.1);
            
            // Update LFO frequency for amplitude modulation
            if (this.lfoOscillator) {
              this.lfoOscillator.frequency.cancelScheduledValues(currentTime);
              this.lfoOscillator.frequency.setTargetAtTime(currentPoint.brainwaveFreq, currentTime, 0.1);
            }
            if (this.lfoGain) {
              this.lfoGain.gain.cancelScheduledValues(currentTime);
              const amplitudeGain = currentPoint.amplitude * this.trackVolume;
              this.lfoGain.gain.setTargetAtTime(amplitudeGain * 0.2, currentTime, 0.1);
            }
            if (this.lfoOffset) {
              this.lfoOffset.offset.cancelScheduledValues(currentTime);
              const amplitudeGain = currentPoint.amplitude * this.trackVolume;
              this.lfoOffset.offset.setTargetAtTime(amplitudeGain * 0.8, currentTime, 0.1);
            }
            break;
            
          case 'isochronic':
            // Both channels get same carrier frequency
            this.leftOscillator.frequency.setTargetAtTime(currentPoint.carrierFreq, currentTime, 0.1);
            this.rightOscillator.frequency.setTargetAtTime(currentPoint.carrierFreq, currentTime, 0.1);
            
            // Update LFO frequency for isochronic pulse rate (half frequency for one pulse per cycle)
            if (this.lfoOscillator) {
              this.lfoOscillator.frequency.cancelScheduledValues(currentTime);
              this.lfoOscillator.frequency.setTargetAtTime(currentPoint.brainwaveFreq / 2, currentTime, 0.1);
            }
            if (this.lfoGain) {
              this.lfoGain.gain.cancelScheduledValues(currentTime);
              this.lfoGain.gain.setTargetAtTime(currentPoint.amplitude * this.trackVolume, currentTime, 0.1); // Scale by amplitude and track volume
            }
            break;
            
          case 'frequency':
            // Update carrier frequency and LFO parameters for frequency modulation
            this.leftOscillator.frequency.setTargetAtTime(currentPoint.carrierFreq, currentTime, 0.1);
            this.rightOscillator.frequency.setTargetAtTime(currentPoint.carrierFreq, currentTime, 0.1);
            
            if (this.leftGain && this.rightGain) {
              const gain = currentPoint.amplitude * this.trackVolume;
              this.leftGain.gain.setTargetAtTime(gain, currentTime, 0.1);
              this.rightGain.gain.setTargetAtTime(gain, currentTime, 0.1);
            }
            
            // Update LFO frequency for frequency modulation
            if (this.lfoOscillator) {
              this.lfoOscillator.frequency.cancelScheduledValues(currentTime);
              this.lfoOscillator.frequency.setTargetAtTime(currentPoint.brainwaveFreq, currentTime, 0.1);
            }
            if (this.lfoGain) {
              this.lfoGain.gain.cancelScheduledValues(currentTime);
              this.lfoGain.gain.setTargetAtTime(10, currentTime, 0.1); // 10Hz modulation depth
            }
            break;
            
          case 'stereo':
            // Update carrier frequency for stereo modulation
            this.leftOscillator.frequency.setTargetAtTime(currentPoint.carrierFreq, currentTime, 0.1);
            this.rightOscillator.frequency.setTargetAtTime(currentPoint.carrierFreq, currentTime, 0.1);
            
            // Set base gain levels (50% each since LFO will modulate around this)
            if (this.leftGain && this.rightGain) {
              const baseGain = currentPoint.amplitude * this.trackVolume * 0.5;
              this.leftGain.gain.setTargetAtTime(baseGain, currentTime, 0.1);
              this.rightGain.gain.setTargetAtTime(baseGain, currentTime, 0.1);
            }
            
            // Update LFO frequency for stereo panning rate
            if (this.lfoOscillator) {
              this.lfoOscillator.frequency.cancelScheduledValues(currentTime);
              this.lfoOscillator.frequency.setTargetAtTime(currentPoint.brainwaveFreq, currentTime, 0.1);
            }
            if (this.lfoGain) {
              this.lfoGain.gain.cancelScheduledValues(currentTime);
              this.lfoGain.gain.setTargetAtTime(0.25, currentTime, 0.1); // 25% modulation depth
            }
            break;
        }
      } catch (error) {
        console.warn('Could not update oscillator parameters in real-time:', error);
      }
    }
  }


  getInterpolatedValues(time: number): { carrierFreq: number; brainwaveFreq: number; amplitude: number } {
    const sortedPoints = [...this.controlPoints].sort((a, b) => a.time - b.time);
    
    if (sortedPoints.length === 0) {
      return { carrierFreq: 200, brainwaveFreq: 10, amplitude: 0.3 };
    }
    
    if (time <= sortedPoints[0].time) {
      return {
        carrierFreq: sortedPoints[0].carrierFreq,
        brainwaveFreq: sortedPoints[0].brainwaveFreq,
        amplitude: sortedPoints[0].amplitude
      };
    }
    
    if (time >= sortedPoints[sortedPoints.length - 1].time) {
      const lastPoint = sortedPoints[sortedPoints.length - 1];
      return {
        carrierFreq: lastPoint.carrierFreq,
        brainwaveFreq: lastPoint.brainwaveFreq,
        amplitude: lastPoint.amplitude
      };
    }
    
    // Find interpolation points
    for (let i = 0; i < sortedPoints.length - 1; i++) {
      const p1 = sortedPoints[i];
      const p2 = sortedPoints[i + 1];
      
      if (time >= p1.time && time <= p2.time) {
        const ratio = (time - p1.time) / (p2.time - p1.time);
        return {
          carrierFreq: p1.carrierFreq + (p2.carrierFreq - p1.carrierFreq) * ratio,
          brainwaveFreq: p1.brainwaveFreq + (p2.brainwaveFreq - p1.brainwaveFreq) * ratio,
          amplitude: p1.amplitude + (p2.amplitude - p1.amplitude) * ratio
        };
      }
    }
    
    return { carrierFreq: 200, brainwaveFreq: 10, amplitude: 0.3 };
  }
}